/**
 * The Barah brand design tokens, ported from the corporate design guide.
 *
 * Barah Pay is one product inside the Barah family, so it does not invent a palette: Navy, Gold and Azure
 * are the corporate colours, and the platform reads as a sibling of Meezan 360 and Rikaz rather than as a
 * separate product. Keeping the values here — rather than scattered through components — is what lets the
 * Ant Design theme, the raw CSS and the checkout page all agree.
 */

/** Navy, Gold and Azure: trust, accent, secondary accent. */
export const BarahColors = {
  navy: '#0B1426',
  charcoal: '#1A2332',
  slate: '#2A3A4E',

  gold: '#D4A843',
  goldLight: '#E8C76A',

  azure: '#3B82C4',
  silver: '#A8B4C4',
} as const;

/** Gold, the primary scale. 500 is the brand accent. */
export const Gold = {
  50: '#FDF8EC',
  100: '#F9EDC9',
  200: '#F2DA9A',
  300: '#E8C36B',
  400: '#DCAE4D',
  500: '#D4A03C',
  600: '#B8862A',
  700: '#946A1F',
  800: '#705016',
  900: '#4D370F',
} as const;

/** Azure, the secondary scale. */
export const Azure = {
  50: '#EFF6FD',
  100: '#DCEDFB',
  200: '#B9D9F6',
  300: '#8FC2EF',
  400: '#5DA2D8',
  500: '#3B82C4',
  600: '#2E69A3',
  700: '#245284',
  800: '#1D4168',
  900: '#183654',
} as const;

export const Semantic = {
  success: '#16A34A',
  successDark: '#34D399',
  warning: '#F59E0B',
  warningDark: '#FBBF24',
  error: '#EF4444',
  errorDark: '#F87171',
  info: '#3B82F6',
} as const;

/** Light-mode surface and text tokens. */
export const LightTheme = {
  background: '#FAFBFD',
  backgroundSecondary: '#F0F2F7',
  surface: '#FFFFFF',
  surfaceElevated: '#FFFFFF',
  textPrimary: '#0B1426',
  textSecondary: '#4A5568',
  textMuted: '#8896A6',
  border: '#E2E6EE',
  borderLight: '#F0F2F7',
  cardShadow: 'rgba(11, 20, 38, 0.06)',
  overlay: 'rgba(11, 20, 38, 0.65)',
} as const;

/** Dark-mode surface and text tokens. */
export const DarkTheme = {
  background: '#0B1426',
  backgroundSecondary: '#121E30',
  surface: '#1A2332',
  surfaceElevated: '#2A3A4E',
  textPrimary: '#F0F2F7',
  textSecondary: '#A8B4C4',
  textMuted: '#5A6A7E',
  border: '#2A3A4E',
  borderLight: '#1A2332',
  cardShadow: 'rgba(0, 0, 0, 0.4)',
  overlay: 'rgba(0, 0, 0, 0.8)',
} as const;

export const Radius = {
  sm: '0.375rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
  '2xl': '1.5rem',
  full: '9999px',
} as const;

export const Shadow = {
  xs: '0 1px 2px 0 rgba(11, 20, 38, 0.04)',
  sm: '0 1px 3px 0 rgba(11, 20, 38, 0.06), 0 1px 2px -1px rgba(11, 20, 38, 0.06)',
  md: '0 4px 6px -1px rgba(11, 20, 38, 0.07), 0 2px 4px -2px rgba(11, 20, 38, 0.05)',
  lg: '0 10px 15px -3px rgba(11, 20, 38, 0.08), 0 4px 6px -4px rgba(11, 20, 38, 0.05)',
  xl: '0 20px 25px -5px rgba(11, 20, 38, 0.1), 0 8px 10px -6px rgba(11, 20, 38, 0.06)',
  gold: '0 4px 14px -3px rgba(212, 168, 67, 0.35)',
  azure: '0 4px 14px -3px rgba(59, 130, 196, 0.35)',
} as const;

/**
 * Cairo carries both scripts, so the same family serves Arabic and English and a language switch does not
 * change the shape of the page. Inter is kept for Latin-only surfaces where its numerals read better.
 */
export const Typography = {
  fontFamily:
    "'Cairo', 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  fontFamilyLatin:
    "'Inter', 'Cairo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontSizeBase: 14,
  lineHeightBase: 1.6,
} as const;

/** The brand gradient: Gold to Azure at 135 degrees. */
export const BrandGradient = `linear-gradient(135deg, ${BarahColors.gold}, ${BarahColors.azure})`;

/**
 * Emitted as CSS custom properties so plain CSS (the checkout app) and Ant Design (the admin portal) draw
 * from one source. Returned as a declaration string for a `:root` block.
 */
export function cssVariables(theme: 'light' | 'dark' = 'light'): string {
  const surfaces = theme === 'dark' ? DarkTheme : LightTheme;

  const entries: Record<string, string> = {
    '--bp-navy': BarahColors.navy,
    '--bp-charcoal': BarahColors.charcoal,
    '--bp-slate': BarahColors.slate,
    '--bp-gold': BarahColors.gold,
    '--bp-gold-light': BarahColors.goldLight,
    '--bp-azure': BarahColors.azure,
    '--bp-silver': BarahColors.silver,

    '--bp-background': surfaces.background,
    '--bp-background-secondary': surfaces.backgroundSecondary,
    '--bp-surface': surfaces.surface,
    '--bp-surface-elevated': surfaces.surfaceElevated,
    '--bp-text': surfaces.textPrimary,
    '--bp-text-secondary': surfaces.textSecondary,
    '--bp-text-muted': surfaces.textMuted,
    '--bp-border': surfaces.border,
    '--bp-border-light': surfaces.borderLight,
    '--bp-card-shadow': surfaces.cardShadow,
    '--bp-overlay': surfaces.overlay,

    '--bp-success': theme === 'dark' ? Semantic.successDark : Semantic.success,
    '--bp-warning': theme === 'dark' ? Semantic.warningDark : Semantic.warning,
    '--bp-error': theme === 'dark' ? Semantic.errorDark : Semantic.error,

    '--bp-radius-sm': Radius.sm,
    '--bp-radius-md': Radius.md,
    '--bp-radius-lg': Radius.lg,
    '--bp-radius-xl': Radius.xl,
    '--bp-radius-2xl': Radius['2xl'],
    '--bp-radius-full': Radius.full,

    '--bp-shadow-xs': Shadow.xs,
    '--bp-shadow-sm': Shadow.sm,
    '--bp-shadow-md': Shadow.md,
    '--bp-shadow-lg': Shadow.lg,
    '--bp-shadow-xl': Shadow.xl,
    '--bp-shadow-gold': Shadow.gold,

    '--bp-gradient': BrandGradient,
    '--bp-font': Typography.fontFamily,
  };

  return Object.entries(entries)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
}
